import {
  ContextChangesetBase,
  CustomerEvalSuiteVersionId,
  OrgGuideFileVersionId,
  SemanticProjectChangeStatus,
  SemanticProjectId,
  WORKSPACE_CONTEXT_FILE_PATH,
  typedObjectFromEntries,
} from "@hex/common";
import { keyBy } from "lodash";

import { ORG_ID } from "../../../../orgs";
import { selectContextWorkbench } from "../../../../redux/slices/contextWorkbenchSlice";
import { RootState } from "../../../../redux/store";
import { createSelector } from "../../../../redux/utils/index.js";
import { WorkbenchDiffStatus } from "../../../workbench/state/diffStatus";
import { PublishContextChangesetFragment } from "../../queries/useInitPublishContextChangeset.generated";
import { CustomerEvalSuiteWithBaseVersionFragment } from "../../queries/useInitPublishContextChangesetBaseEvalFiles.generated";
import { OrgGuideFileWithBaseVersionFragment } from "../../queries/useInitPublishContextChangesetBaseGuideFiles.generated";
import { ContextWorkbenchEntity } from "../contextWorkbenchEntity";
import { ContextWorkbenchFileId } from "../contextWorkbenchFile";
import { ContextWorkbenchPublish } from "../contextWorkbenchPublish";
import { ContextWorkbenchUnifiedPreview } from "../contextWorkbenchUnifiedPreview";
import {
  getEditorFilesListNodeForAsyncPlaceholder,
  getEditorFilesListNodeForFile,
} from "../utils/editorFilesListNode";
import { fileComparator, getFileForEntity } from "../utils/file";
import {
  buildEvalSuiteFileForChange,
  buildEvalSuiteFileGroup,
  buildOrgGuideFileForChange,
  buildOrgGuideFileGroup,
  buildSemanticProjectGroups,
  countEditedFiles,
  getGroupSemanticProjectId,
} from "../utils/publishFile";
import {
  getContextWorkbenchFileNodeGroups,
  getContextWorkbenchFileNodeGroupsAsyncPlaceholder,
} from "../utils/sources";

import { ContextWorkbenchSelectorOptions as SelectorOptions } from "./options";

type SliceState = Pick<RootState, "contextWorkbench">;

const selectPublishFiles = createSelector(
  [
    (s: SliceState) => selectContextWorkbench(s).publish.changeset,
    (s: SliceState) => selectContextWorkbench(s).publish.baseGuideFiles,
  ],
  (changeset, baseGuideFiles) => {
    if (!changeset.value || !baseGuideFiles.value) {
      return [];
    }
    const orgGuideFileChangesByFileId = keyBy(
      changeset.value.orgGuideFileChanges,
      (
        change: PublishContextChangesetFragment["orgGuideFileChanges"][number],
      ) => change.orgGuideFile.id,
    );
    const changesetBase = changeset.value.base;
    const orgId = changeset.value.orgId;
    const getBaseVersionForFile = (
      orgGuideFile: OrgGuideFileWithBaseVersionFragment,
    ):
      | OrgGuideFileWithBaseVersionFragment["latestVersion"]
      | OrgGuideFileWithBaseVersionFragment["draftVersion"] => {
      if (changesetBase === ContextChangesetBase.DRAFT) {
        return orgGuideFile.draftVersion;
      } else {
        return orgGuideFile.latestVersion;
      }
    };

    const files = baseGuideFiles.value
      .map((baseGuideFile) => {
        const maybeChange = orgGuideFileChangesByFileId[baseGuideFile.id];
        if (!maybeChange) {
          const baseVersion = getBaseVersionForFile(baseGuideFile);
          const file = getFileForEntity({
            type: ContextWorkbenchEntity.Type.OrgGuideFile,
            contents: baseVersion?.contentsText ?? null,
            filePath: baseGuideFile.filePath,
            orgId: ORG_ID,
            orgGuideFileId: baseGuideFile.id,
            orgGuideFileVersionId:
              baseVersion?.id ??
              OrgGuideFileVersionId.check(`#deleted:${baseGuideFile.id}`),
            externalSource: baseVersion?.externalSource ?? null,
          });
          return {
            ...file,
            diff: {
              status: WorkbenchDiffStatus.UNMODIFIED,
              baseContents: baseVersion?.contentsText ?? null,
            },
            externalSource: baseVersion?.externalSource ?? null,
          };
        } else {
          return buildOrgGuideFileForChange(maybeChange, orgId);
        }
      })
      .filter(
        // baseGuideFiles also includes deleted files meaning we also need to filter out files that are deleted that have no diff
        (changes) =>
          !(
            changes.diff.status === WorkbenchDiffStatus.UNMODIFIED &&
            changes.contents == null
          ),
      )
      .sort(fileComparator);
    return files;
  },
);

const selectPublishEvalFiles = createSelector(
  [
    (s: SliceState) => selectContextWorkbench(s).publish.changeset,
    (s: SliceState) => selectContextWorkbench(s).publish.baseEvalFiles,
  ],
  (changeset, baseEvalFiles) => {
    if (!changeset.value || !baseEvalFiles.value) {
      return [];
    }

    const orgId = changeset.value.orgId;
    const evalSuiteChangesBySuiteId = keyBy(
      changeset.value.evalSuiteChanges,
      (change: PublishContextChangesetFragment["evalSuiteChanges"][number]) =>
        change.evalSuite.id,
    );
    const baseSuiteIds = new Set(
      baseEvalFiles.value.map(
        (suite: CustomerEvalSuiteWithBaseVersionFragment) => suite.id,
      ),
    );

    const filesFromBase = baseEvalFiles.value
      .map((baseSuite) => {
        const maybeChange = evalSuiteChangesBySuiteId[baseSuite.id];
        if (maybeChange) {
          return buildEvalSuiteFileForChange(maybeChange, orgId);
        }

        const latestVersion = baseSuite.latestVersion;
        const file = getFileForEntity({
          type: ContextWorkbenchEntity.Type.EvalSuiteFile,
          orgId,
          customerEvalSuiteId: baseSuite.id,
          customerEvalSuiteVersionId:
            latestVersion?.id ??
            CustomerEvalSuiteVersionId.check(`#deleted:${baseSuite.id}`),
          filePath: `${baseSuite.publicIdentifier}.yaml`,
          contents: latestVersion?.rawDefinition ?? null,
          externalSource: latestVersion?.externalSource ?? null,
        });
        return {
          ...file,
          diff: {
            status: WorkbenchDiffStatus.UNMODIFIED,
            baseContents: latestVersion?.rawDefinition ?? null,
          },
          externalSource: latestVersion?.externalSource ?? null,
        };
      })
      .filter(
        (file) =>
          !(
            file.diff.status === WorkbenchDiffStatus.UNMODIFIED &&
            file.contents == null
          ),
      );

    // Suites missing from the ACTIVE base list still appear via the changeset —
    // newly created suites (not published yet) and updates to archived suites.
    const filesFromChangesetOnly = changeset.value.evalSuiteChanges
      .filter((change) => !baseSuiteIds.has(change.evalSuite.id))
      .map((change) => buildEvalSuiteFileForChange(change, orgId));

    return [...filesFromBase, ...filesFromChangesetOnly].sort(fileComparator);
  },
);

const selectVisibleFiles = createSelector(
  [
    selectPublishFiles,
    (s: SliceState) => selectContextWorkbench(s).publish.changesFilter,
    (_s: SliceState, options: SelectorOptions) => options,
  ],
  (files, changesFilter, options) => {
    return files
      .filter((file) => {
        if (!options.replaceWorkspaceRulesEnabled) {
          return file.filepath !== WORKSPACE_CONTEXT_FILE_PATH;
        }
        return true;
      })
      .filter((file) =>
        changesFilter === ContextWorkbenchPublish.ChangesFilter.CHANGED
          ? file.diff.status !== WorkbenchDiffStatus.UNMODIFIED
          : true,
      );
  },
);

const selectPublishFilesById = createSelector([selectPublishFiles], (files) => {
  return typedObjectFromEntries(files.map((file) => [file.id, file]));
});

const selectSelectedPublishFile = createSelector(
  [
    selectPublishFilesById,
    (s: SliceState) => selectContextWorkbench(s).publish.selectedFileId,
  ],
  (filesById, selectedFileId) => {
    return selectedFileId ? (filesById[selectedFileId] ?? null) : null;
  },
);

const selectPublishFileListNodesBySource = createSelector(
  [
    (s: SliceState) => selectContextWorkbench(s).publish.changeset,
    selectVisibleFiles,
  ],
  (changeset, visibleFiles) => {
    if (changeset.loading || changeset.error) {
      const node = getEditorFilesListNodeForAsyncPlaceholder(
        ContextWorkbenchEntity.Type.OrgGuideFile,
        changeset.loading,
        changeset.error,
      );
      return getContextWorkbenchFileNodeGroupsAsyncPlaceholder(node);
    } else {
      const nodes = visibleFiles.map((file) => ({
        ...getEditorFilesListNodeForFile(file.id),
        externalSource: file.externalSource,
      }));

      return getContextWorkbenchFileNodeGroups(nodes);
    }
  },
);

const selectFirstVisibleFile = createSelector([selectVisibleFiles], (files) => {
  return (
    files.find((f) => f.diff.status !== WorkbenchDiffStatus.UNMODIFIED)?.id ??
    files[0]?.id ??
    null
  );
});

const selectPublishFormData = (s: SliceState) =>
  selectContextWorkbench(s).publish.formData;

const selectUnifiedPreviewResources = createSelector(
  [
    (s: SliceState) => selectContextWorkbench(s).publish.changeset.value,
    selectPublishFiles,
    selectPublishEvalFiles,
    (s: SliceState) =>
      selectContextWorkbench(s).publish.baseEvalFiles.isTruncated,
    (s: SliceState) => selectContextWorkbench(s).publish.changesFilter,
  ],
  (
    changeset,
    orgGuideFiles,
    evalFiles,
    evalFilesTruncated,
    changesFilter,
  ): ContextWorkbenchUnifiedPreview.Resources => {
    if (changeset == null) {
      return { groups: [], editedFileCount: 0 };
    }

    const orgGuideGroup = buildOrgGuideFileGroup(orgGuideFiles);
    const evalSuiteGroup = buildEvalSuiteFileGroup(
      evalFiles,
      evalFilesTruncated &&
        changesFilter === ContextWorkbenchPublish.ChangesFilter.ALL
        ? `Not all evals have been loaded.`
        : undefined,
    );
    const groups: ContextWorkbenchUnifiedPreview.ResourceGroup[] = [
      ...(orgGuideGroup ? [orgGuideGroup] : []),
      ...buildSemanticProjectGroups(changeset),
      ...(evalSuiteGroup ? [evalSuiteGroup] : []),
    ];

    return { groups, editedFileCount: countEditedFiles(groups) };
  },
);

const selectSemanticProjectsForReferences = createSelector(
  [selectUnifiedPreviewResources],
  ({ groups }): { id: SemanticProjectId; name: string }[] =>
    groups.flatMap((group) => {
      const id = getGroupSemanticProjectId(group);
      return id != null ? [{ id, name: group.label }] : [];
    }),
);

const selectChangesetReady = (s: SliceState): boolean => {
  const { baseEvalFiles, baseGuideFiles, changeset } =
    selectContextWorkbench(s).publish;
  return (
    changeset.value != null &&
    baseGuideFiles.value != null &&
    baseEvalFiles.value != null
  );
};

const selectUnifiedPreviewVisibleGroups = createSelector(
  [
    selectUnifiedPreviewResources,
    (s: SliceState) => selectContextWorkbench(s).publish.changesFilter,
    (_s: SliceState, options: SelectorOptions) => options,
  ],
  (
    { groups },
    changesFilter,
    options,
  ): ContextWorkbenchUnifiedPreview.ResourceGroup[] => {
    const showChangedOnly =
      changesFilter === ContextWorkbenchPublish.ChangesFilter.CHANGED;
    return groups
      .map((group): ContextWorkbenchUnifiedPreview.ResourceGroup => {
        if (
          group.type ===
          ContextWorkbenchUnifiedPreview.ResourceGroup.Type.SemanticProject
        ) {
          return group;
        }
        const files = group.files
          .filter((file) =>
            options.replaceWorkspaceRulesEnabled
              ? true
              : file.filepath !== WORKSPACE_CONTEXT_FILE_PATH,
          )
          .filter((file) =>
            showChangedOnly
              ? file.diff.status !== WorkbenchDiffStatus.UNMODIFIED
              : true,
          );
        return { ...group, files };
      })
      .filter((group) => {
        if (
          group.type ===
          ContextWorkbenchUnifiedPreview.ResourceGroup.Type.SemanticProject
        ) {
          return showChangedOnly
            ? group.status === SemanticProjectChangeStatus.MODIFIED
            : true;
        }
        return group.files.length > 0;
      });
  },
);

export const contextWorkbenchPublishSelectors = {
  selectIsPublishing: (s: SliceState) =>
    selectContextWorkbench(s).view === "publish",
  selectView: (s: SliceState) => selectContextWorkbench(s).publish.view,
  selectBaseGuideFiles: (s: SliceState) =>
    selectContextWorkbench(s).publish.baseGuideFiles,
  selectChangeset: (s: SliceState) =>
    selectContextWorkbench(s).publish.changeset,
  selectLoadedChangeset: (s: SliceState) =>
    contextWorkbenchPublishSelectors.selectChangeset(s).value,
  selectAskPreviewThread: (s: SliceState) =>
    selectContextWorkbench(s).publish.askPreviewThread,
  selectSelectedEvalRunId: (s: SliceState) =>
    selectContextWorkbench(s).publish.selectedEvalRunId,
  selectFiles: selectPublishFiles,
  selectVisibleFiles: selectVisibleFiles,
  selectFilesById: selectPublishFilesById,
  selectFileById: (fileId: ContextWorkbenchFileId) => (s: SliceState) =>
    selectPublishFilesById(s)[fileId],
  selectFirstVisibleFile: selectFirstVisibleFile,
  selectSelectedFile: selectSelectedPublishFile,
  selectFileListNodesBySource: selectPublishFileListNodesBySource,
  selectFormData: selectPublishFormData,
  selectChangesetReady: selectChangesetReady,
  selectUnifiedPreviewResources: selectUnifiedPreviewResources,
  selectUnifiedPreviewVisibleGroups: selectUnifiedPreviewVisibleGroups,
  selectSemanticProjectsForReferences: selectSemanticProjectsForReferences,
};
